import { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  TextField,
  Tabs,
  Tab,
  Alert,
  Divider
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import { getMockInTransitPayments, formatCurrency } from "../../../../../utils/mockWalletData";

/**
 * ReconciliationDashboard - Track payments after they leave Thaklis bank
 *
 * Shows payments in 3 states:
 * - Normal (0-2 days): Green - Within expected timeline
 * - Follow-up (3-5 days): Yellow - Should send reminder
 * - Overdue (>5 days): Red - Investigation required
 */
const ReconciliationDashboard = () => {
  const payments = getMockInTransitPayments();
  const [filter, setFilter] = useState('all'); // 'all', 'normal', 'followup', 'overdue'
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState({});

  // Filter payments
  const filteredPayments = payments.filter(payment => {
    const matchesFilter = filter === 'all' ||
      (filter === 'normal' && payment.reconciliation.reconciliationStatus === 'NORMAL') ||
      (filter === 'followup' && payment.reconciliation.reconciliationStatus === 'FOLLOW_UP_RECOMMENDED') ||
      (filter === 'overdue' && payment.reconciliation.reconciliationStatus === 'OVERDUE');

    const matchesSearch = !searchTerm ||
      payment.partnerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.id.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  // Count by status
  const statusCounts = {
    normal: payments.filter(p => p.reconciliation.reconciliationStatus === 'NORMAL').length,
    followup: payments.filter(p => p.reconciliation.reconciliationStatus === 'FOLLOW_UP_RECOMMENDED').length,
    overdue: payments.filter(p => p.reconciliation.reconciliationStatus === 'OVERDUE').length
  };

  const totalInTransit = payments.reduce((sum, p) => sum + p.amount, 0);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'NORMAL':
        return 'success';
      case 'FOLLOW_UP_RECOMMENDED':
        return 'warning';
      case 'OVERDUE':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'NORMAL':
        return <CheckCircleIcon />;
      case 'FOLLOW_UP_RECOMMENDED':
        return <ContactPhoneIcon />;
      case 'OVERDUE':
        return <ReportProblemIcon />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'NORMAL':
        return 'Normal';
      case 'FOLLOW_UP_RECOMMENDED':
        return 'Follow-up';
      case 'OVERDUE':
        return 'OVERDUE';
      default:
        return status;
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Reconciliation Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track payments after they leave Thaklis bank - awaiting partner confirmation
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ p: 2, bgcolor: 'primary.lighter', border: 1, borderColor: 'primary.light' }}>
            <Typography variant="caption" color="text.secondary">Total In Transit</Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {formatCurrency(totalInTransit)}
            </Typography>
            <Typography variant="caption">{payments.length} payments</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ p: 2, bgcolor: 'success.lighter', border: 1, borderColor: 'success.light' }}>
            <Typography variant="caption" color="text.secondary">Normal (0-2 days)</Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
              {statusCounts.normal}
            </Typography>
            <Typography variant="caption">No action needed</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ p: 2, bgcolor: 'warning.lighter', border: 1, borderColor: 'warning.light' }}>
            <Typography variant="caption" color="text.secondary">Follow-up (3-5 days)</Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
              {statusCounts.followup}
            </Typography>
            <Typography variant="caption">Send reminder</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ p: 2, bgcolor: 'error.lighter', border: 1, borderColor: 'error.light' }}>
            <Typography variant="caption" color="text.secondary">OVERDUE (>5 days)</Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.main' }}>
              {statusCounts.overdue}
            </Typography>
            <Typography variant="caption">Investigate</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Tabs
              value={filter}
              onChange={(e, newValue) => setFilter(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label={`All (${payments.length})`} value="all" />
              <Tab
                label={`Normal (${statusCounts.normal})`}
                value="normal"
                icon={<CheckCircleIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                label={`Follow-up (${statusCounts.followup})`}
                value="followup"
                icon={<ContactPhoneIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                label={`Overdue (${statusCounts.overdue})`}
                value="overdue"
                icon={<ReportProblemIcon fontSize="small" />}
                iconPosition="start"
              />
            </Tabs>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by partner name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
        </Grid>
      </Card>

      {/* Payments Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <TableCell width={50} sx={{ color: 'white' }} />
              <TableCell sx={{ color: 'white' }}><strong>Partner</strong></TableCell>
              <TableCell align="right" sx={{ color: 'white' }}><strong>Amount</strong></TableCell>
              <TableCell sx={{ color: 'white' }}><strong>Sent Date</strong></TableCell>
              <TableCell align="center" sx={{ color: 'white' }}><strong>Days</strong></TableCell>
              <TableCell align="center" sx={{ color: 'white' }}><strong>Status</strong></TableCell>
              <TableCell align="center" sx={{ color: 'white' }}><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPayments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No payments found matching your criteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredPayments.map((payment) => (
                <>
                  {/* Main Row */}
                  <TableRow
                    key={payment.id}
                    sx={{
                      bgcolor: payment.reconciliation.reconciliationStatus === 'OVERDUE' ? 'error.lighter' :
                        payment.reconciliation.reconciliationStatus === 'FOLLOW_UP_RECOMMENDED' ? 'warning.lighter' :
                          'inherit',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleRow(payment.id)}>
                        {expandedRows[payment.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {payment.partnerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {payment.id}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {formatCurrency(payment.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(payment.bankTransfer.transferDate)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {payment.bankTransfer.bankName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${payment.reconciliation.daysSincePayment} days`}
                        size="small"
                        color={getStatusColor(payment.reconciliation.reconciliationStatus)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={getStatusIcon(payment.reconciliation.reconciliationStatus)}
                        label={getStatusLabel(payment.reconciliation.reconciliationStatus)}
                        size="small"
                        color={getStatusColor(payment.reconciliation.reconciliationStatus)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton size="small" color="primary" title="Contact Partner">
                          <PhoneIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="primary" title="View Bank Statement">
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="success" title="Confirm Early">
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Details Row */}
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                      <Collapse in={expandedRows[payment.id]} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 3, px: 2, bgcolor: 'grey.50' }}>
                          <Grid container spacing={3}>
                            {/* Left Column - Payment Details */}
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Payment Details
                              </Typography>
                              <Box sx={{ pl: 2 }}>
                                <Typography variant="caption" display="block">
                                  <strong>Transfer Method:</strong> {payment.bankTransfer.transferMethod}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Reference:</strong> {payment.bankTransfer.referenceNumber}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Transferred By:</strong> {payment.bankTransfer.transferredBy} ({payment.bankTransfer.transferredByRole || 'Finance Admin'})
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Bank:</strong> {payment.partnerBankDetails.bankName}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Account:</strong> {payment.partnerBankDetails.accountName} ({payment.partnerBankDetails.iban})
                                </Typography>
                              </Box>

                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                                Related Tickets
                              </Typography>
                              <Box sx={{ pl: 2 }}>
                                {payment.relatedTickets.map((ticket, idx) => (
                                  <Box key={idx} sx={{ mb: 1 }}>
                                    <Typography variant="caption" display="block">
                                      <strong>{ticket.ticketId}:</strong> {ticket.serviceName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Customer: {formatCurrency(ticket.customerPayment)} → Partner: {formatCurrency(ticket.partnerPayment)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Grid>

                            {/* Right Column - Reconciliation Status */}
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Reconciliation Status
                              </Typography>
                              <Box sx={{ pl: 2 }}>
                                <Typography variant="caption" display="block">
                                  <strong>Thaklis Bank:</strong> ✓ Debited on {formatDate(payment.bankTransfer.debitDate)}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Bank Statement:</strong> {payment.reconciliation.thaklisBankStatementMatch ? '✓ Match confirmed' : '⏳ Pending'}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Partner Status:</strong> ❌ No confirmation received
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Auto-confirm Date:</strong> {new Date(payment.reconciliation.autoConfirmDate).toLocaleDateString()}
                                  ({payment.reconciliation.daysUntilAutoConfirm > 0 ? `${payment.reconciliation.daysUntilAutoConfirm} days remaining` : 'PASSED'})
                                </Typography>
                              </Box>

                              {/* Alerts */}
                              {payment.reconciliation.alerts && payment.reconciliation.alerts.length > 0 && (
                                <>
                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                                    Alerts
                                  </Typography>
                                  <Box sx={{ pl: 2 }}>
                                    {payment.reconciliation.alerts.map((alert, idx) => (
                                      <Alert
                                        key={idx}
                                        severity={alert.type.toLowerCase()}
                                        sx={{ mb: 1 }}
                                      >
                                        <Typography variant="caption">{alert.message}</Typography>
                                      </Alert>
                                    ))}
                                  </Box>
                                </>
                              )}

                              {/* Follow-up History */}
                              {payment.reconciliation.followUpHistory && payment.reconciliation.followUpHistory.length > 0 && (
                                <>
                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                                    Follow-up History
                                  </Typography>
                                  <Box sx={{ pl: 2 }}>
                                    {payment.reconciliation.followUpHistory.map((follow, idx) => (
                                      <Box key={idx} sx={{ mb: 1 }}>
                                        <Typography variant="caption" display="block">
                                          <strong>{formatDate(follow.date)}:</strong> {follow.method}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {follow.notes}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </>
                              )}

                              {/* Investigation */}
                              {payment.investigation && payment.investigation.required && (
                                <>
                                  <Divider sx={{ my: 2 }} />
                                  <Alert severity="error">
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                      Investigation Required
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                      <strong>Assigned to:</strong> {payment.investigation.assignedTo} ({payment.investigation.assignedRole})
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                      <strong>Status:</strong> {payment.investigation.status}
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                      <strong>Reason:</strong> {payment.investigation.reason}
                                    </Typography>
                                  </Alert>
                                </>
                              )}
                            </Grid>
                          </Grid>

                          {/* Action Buttons */}
                          <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              startIcon={<PhoneIcon />}
                              variant="outlined"
                            >
                              Contact Partner
                            </Button>
                            <Button
                              size="small"
                              startIcon={<DescriptionIcon />}
                              variant="outlined"
                            >
                              View Bank Statement
                            </Button>
                            {payment.reconciliation.reconciliationStatus === 'OVERDUE' && (
                              <Button
                                size="small"
                                startIcon={<ReportProblemIcon />}
                                variant="outlined"
                                color="error"
                              >
                                Raise Dispute
                              </Button>
                            )}
                            <Button
                              size="small"
                              startIcon={<CheckCircleIcon />}
                              variant="contained"
                              color="success"
                            >
                              Confirm Receipt
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ReconciliationDashboard;
